import NoteFormModern from '@/components/NoteFormModern';
"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/hooks/use-toast';
import { createNote } from '@/lib/supabase'; // Import createNote directly
import { Session } from '@supabase/supabase-js';
import ProductImageManager from '@/components/ProductImageManager';
import { useSession } from '@/context/SessionContext';
import RichTextEditor from '@/components/RichTextEditor';
import LocationInput from '@/components/LocationInput';
import type { NoteLocation } from '@/types/location';
import LocationSelectorModal from '@/components/LocationSelectorModal';
import CategorySelector from '@/components/CategorySelector';
import { useNavigate } from 'react-router-dom';
import { FileText, X } from '@/components/icons';
import useDebounce from '@/hooks/use-debounce';

interface CreateNotePageProps {
  session: Session | null;
}

interface CreateNotePageInternalProps extends CreateNotePageProps {
  initialCategoryId?: string | null;
}
const DRAFT_STORAGE_KEY = 'unsaved-note-draft';

const CreateNotePage: React.FC<CreateNotePageInternalProps> = ({ session: sessionProp, initialCategoryId = null }) => {
  const { session: contextSession, profile, relevantLocation } = useSession();
  const session = sessionProp || contextSession;
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  // El estado de imágenes y mainImage se maneja solo dentro de ProductImageManager
  const imagesRef = useRef<string[]>([]);
  const mainImageRef = useRef<string | null>(null);
  const imagesToDeleteRef = useRef<string[]>([]);
  // Set default location: use relevantLocation from SessionContext (which includes Salta default for non-logged users)
  const [location, setLocation] = useState<NoteLocation | null>(
    relevantLocation
      ? {
          displayName: relevantLocation.displayName,
          latitude: relevantLocation.latitude,
          longitude: relevantLocation.longitude,
        }
      : null
  );
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [locationEditorAutoFocus, setLocationEditorAutoFocus] = useState(false);
  const [editingLocation, setEditingLocation] = useState<NoteLocation | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  // --- Autoguardado ---
  const draftState = { title, content, location, selectedCategoryId };
  const debouncedDraftState = useDebounce(draftState, 500);

  // Cargar borrador al montar el componente
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setTitle(draft.title || '');
        setContent(draft.content || '');
        imagesRef.current = draft.images || [];
        mainImageRef.current = draft.mainImage || null;
        setLocation(draft.location || null);
        // Mostrar toast solo si hay contenido en alguna parte de la nota
        const hasContent = Boolean(
          (draft.title && draft.title.trim()) ||
          (draft.content && draft.content.trim()) ||
          (draft.images && draft.images.length > 0) ||
          (draft.mainImage) ||
          (draft.location && draft.location.displayName) ||
          (draft.selectedCategoryId)
        );
        if (hasContent) {
          toast({
            title: "Borrador restaurado",
            description: "Hemos recuperado tu nota no guardada.",
          });
        }
      } catch (error) {
        console.error("Error parsing saved draft:", error);
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
    // If there's no saved draft category, initialize from prop
    // (aquí puedes inicializar la categoría si es necesario)
  }, [toast]);

  // Guardar borrador cuando el estado cambie
  useEffect(() => {
    // Solo guardar si hay algo que guardar
    if (debouncedDraftState.title || debouncedDraftState.content || debouncedDraftState.photoUrl) {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(debouncedDraftState));
    }
    // No limpiar imágenes aquí, el manager las maneja internamente
  }, [debouncedDraftState]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setTitle('');
    setContent('');
    imagesRef.current = [];
    mainImageRef.current = null;
    setLocation(null);
    setSelectedCategoryId(null);
  }, []);

  const handleClearDraft = () => {
    clearDraft();
    toast({
      title: "Formulario limpiado",
      description: "El formulario ha sido limpiado y el borrador descartado.",
    });
  };
  // --- Fin Autoguardado ---

  const publishNote = async () => {
    toast({ title: 'DEBUG', description: 'Intentando publicar nota...' });
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para publicar una nota.",
        variant: "destructive",
      });
      toast({ title: 'DEBUG', description: 'No hay sesión de usuario.' });
      return;
    }
    try {
      const payload = {
        userId: session.user.id,
        title,
        content,
        mainImage: mainImageRef.current,
        images: imagesRef.current,
        locationDisplayName: location?.displayName || null,
        latitude: location?.latitude || null,
        longitude: location?.longitude || null,
        selectedCategoryId
      };
      toast({ title: 'DEBUG', description: 'Payload: ' + JSON.stringify(payload) });
      await createNote(
        payload.userId,
        payload.title,
        payload.content,
        payload.mainImage,
        payload.images,
        payload.locationDisplayName,
        payload.latitude,
        payload.longitude,
        payload.selectedCategoryId
      );

      toast({
        title: "Nota publicada",
        description: "Tu nota ha sido publicada exitosamente.",
      });
      toast({ title: 'DEBUG', description: 'Nota publicada correctamente.' });
      clearDraft(); // Limpiar el borrador después de publicar
      navigate('/notas');
    } catch (error) {
      console.error("Error creating note:", error);
      toast({
        title: "Error al publicar nota",
        description: "No se pudo publicar la nota. Inténtalo de nuevo.",
        variant: "destructive",
      });
      toast({ title: 'DEBUG', description: 'Error: ' + (error?.message || JSON.stringify(error)) });
    toast({ title: 'DEBUG', description: 'Click en Publicar Nota' });
    } finally {
      setLoading(false);
      toast({ title: 'DEBUG', description: 'Finalizó publishNote.' });
    }
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "El título de la nota no puede estar vacío.",
        variant: "destructive",
      });
      return;
    }

    const isContentEmpty = !content.trim() || content === '<p></p>';
    const isPhotoEmpty = !photoUrl;
    const isLocationEmpty = !location?.displayName;
    const isCategoryEmpty = !selectedCategoryId;

    if (isContentEmpty && isPhotoEmpty && isLocationEmpty && isCategoryEmpty) {
      setConfirmationMessage("El contenido de la nota, la imagen, la ubicación y la categoría están vacíos. ¿Deseas publicar de todos modos?");
      setShowConfirmationDialog(true);
    } else if (isContentEmpty && isPhotoEmpty && isCategoryEmpty) {
      setConfirmationMessage("El contenido de la nota, la imagen y la categoría están vacíos. ¿Deseas publicar de todos modos?");
      setShowConfirmationDialog(true);
    } else if (isContentEmpty && isPhotoEmpty) {
      setConfirmationMessage("El contenido de la nota y la imagen están vacíos. ¿Deseas publicar de todos modos?");
      setShowConfirmationDialog(true);
    } else if (isContentEmpty) {
      setConfirmationMessage("El contenido de la nota está vacío. ¿Deseas publicar de todos modos?");
      setShowConfirmationDialog(true);
    } else if (isPhotoEmpty) {
      setConfirmationMessage("No seleccionaste una imagen. ¿Deseas publicar la nota sin imagen?");
      setShowConfirmationDialog(true);
    } else if (isLocationEmpty) {
      setConfirmationMessage("No especificaste una ubicación. ¿Deseas publicar la nota sin ubicación?");
      setShowConfirmationDialog(true);
    } else if (isCategoryEmpty) {
      setConfirmationMessage("No seleccionaste una categoría. ¿Deseas publicar la nota sin categoría?");
      setShowConfirmationDialog(true);
    }
    else {
      publishNote();
    }
  };

  const handleConfirmPublish = () => {
    setShowConfirmationDialog(false);
    publishNote();
  };

  const handleNoteSubmit = async (note) => {
    setLoading(true);
    try {
      const payload = {
        userId: session.user.id,
        title: note.title,
        content: note.content,
        mainImage: note.mainImage,
        images: note.images,
        locationDisplayName: note.location?.displayName || null,
        latitude: note.location?.latitude || null,
        longitude: note.location?.longitude || null,
        selectedCategoryId: note.categoryId,
      };
      await createNote(
        payload.userId,
        payload.title,
        payload.content,
        payload.mainImage,
        payload.images,
        payload.locationDisplayName,
        payload.latitude,
        payload.longitude,
        payload.selectedCategoryId
      );
      toast({ title: 'Nota publicada', description: 'Tu nota ha sido publicada exitosamente.' });
      clearDraft();
      navigate('/notas');
    } catch (error) {
      toast({ title: 'Error al publicar nota', description: 'No se pudo publicar la nota. Inténtalo de nuevo.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <NoteFormModern onSubmit={handleNoteSubmit} loading={loading} />
  );
};

export default CreateNotePage;